import { PROXY_HINTS } from '../../inventorySyncHints';
import { ProxyStatus } from '../../../../models/inventorySync';
import { parseUtcDate } from '../../../../common/dateTime';
import { shortProcessName } from '../../inventorySyncOverview';

type UtcValue = string | null | undefined;

/** Длительность в целых секундах: «45 с», «1 мин 5 с», «2 ч 30 мин» */
function formatDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
        return minutes > 0 ? `${hours} ч ${minutes} мин` : `${hours} ч`;
    }
    if (minutes > 0) {
        return seconds > 0 ? `${minutes} мин ${seconds} с` : `${minutes} мин`;
    }
    return `${seconds} с`;
}

function msFromNow(value: string, nowMs: number): number {
    return parseUtcDate(value).getTime() - nowMs;
}

/** Через сколько закончится пауза прокси: «через 13 с», «сейчас»; «—» — запросов ещё не было */
export function formatNextRequest(nextRequestAt: UtcValue, nowMs: number): string {
    if (!nextRequestAt) {
        return '—';
    }
    const left = Math.ceil(msFromNow(nextRequestAt, nowMs) / 1000);
    return left > 0 ? `через ${formatDuration(left)}` : 'сейчас';
}

/** Остаток до момента (cooldown после 429); «—» — уже прошёл или не задан */
export function formatTimeLeft(until: UtcValue, nowMs: number): string {
    if (!until) {
        return '—';
    }
    const left = Math.ceil(msFromNow(until, nowMs) / 1000);
    return left > 0 ? formatDuration(left) : '—';
}

/** Сколько прошло с момента: «12 с назад»; время чуть впереди (часы процессов) — «только что» */
export function formatAgo(at: UtcValue, nowMs: number): string {
    if (!at) {
        return '—';
    }
    const ago = Math.floor(-msFromNow(at, nowMs) / 1000);
    return ago > 0 ? `${formatDuration(ago)} назад` : 'только что';
}

/** Запросов за прошлую минуту против лимита: «3 / 4»; выше лимита — overLimit; null — метрики недоступны */
export function formatRequestsPerMinute(
    requests: number | null | undefined,
    limit: number | null | undefined
): { text: string; overLimit: boolean } {
    if (requests === null || requests === undefined) {
        return { text: '—', overLimit: false };
    }
    if (!limit) {
        return { text: String(requests), overLimit: false };
    }
    return { text: `${requests} / ${limit}`, overLimit: requests > limit };
}

/** Процессы, использовавшие прокси: «BackgroundService, Bots» */
export function formatUsedBy(usedBy: string[] | null | undefined): string {
    return usedBy && usedBy.length > 0 ? usedBy.map(shortProcessName).join(', ') : '—';
}

export function sortProxies(proxies: ProxyStatus[]): ProxyStatus[] {
    return [...proxies].sort((a, b) => {
        if (a.isDisabled !== b.isDisabled) {
            return a.isDisabled ? 1 : -1;
        }
        if (a.isBusy !== b.isBusy) {
            return a.isBusy ? -1 : 1;
        }
        return b.score - a.score;
    });
}

export function getProxyStatusLabel(proxy: ProxyStatus): {
    label: string;
    color: 'success' | 'warning' | 'danger' | 'primary' | 'neutral';
    hint: string;
} {
    if (proxy.isDisabled) {
        return { label: 'Отключён', color: 'danger', hint: PROXY_HINTS.isDisabled };
    }
    if (proxy.isResting) {
        return { label: 'Отдых', color: 'warning', hint: PROXY_HINTS.isResting };
    }
    if (proxy.isBusy) {
        return { label: 'Занят', color: 'primary', hint: PROXY_HINTS.isBusy };
    }
    return { label: 'Доступен', color: 'success', hint: 'Прокси свободен, не на отдыхе и не отключён — готов к выдаче воркеру.' };
}
